/**
 * Tenant packs and tenant-key resolution for pathway.
 *
 * From the react-auth v4 / react-ui split, the tenant key string is the only
 * contract shared between authentication (`@webuild-ai/react-auth`) and the
 * visual brand system (`@webuild-ai/react-ui`). This module is the single place
 * pathway declares which tenants it serves; it is imported by both `main.tsx`
 * (for the `BrandProvider` packs) and `vite.config.ts` (for the static asset
 * copy), so adding or removing a tenant is a one-line change in each list.
 */

import type { BrandPack } from "@webuild-ai/react-ui/types";

import {
  getTenantForDomain,
  isKnownTenantKey,
  resolveTenantKey,
} from "@webuild-ai/react-auth/config";
import { lseg } from "@webuild-ai/react-ui/tenants/lseg";
import { nget } from "@webuild-ai/react-ui/tenants/nget";
import { ngv } from "@webuild-ai/react-ui/tenants/ngv";
import { webuild } from "@webuild-ai/react-ui/tenants/webuild";

import { tenantFolders } from "./tenantFolders";

/**
 * Literal union of the tenant asset folders pathway serves, mirroring
 * `./tenantFolders.json`. JSON module imports widen to `string[]` (no literal
 * inference), so the union cannot be derived from that import directly; it is
 * declared once here and checked against the JSON below (and against the
 * packs registered in `tenantPacksByFolder`) so the two representations —
 * this one, and the JSON consumed by `vite.config.ts`'s separate composite
 * project — cannot silently drift apart. `RuntimeBrand.folder` itself stays
 * typed as `string` in react-ui, since it must also carry the neutral
 * `FALLBACK_BRAND`'s `"fallback"` folder for unrecognised tenant keys.
 */
const TENANT_FOLDER_VALUES = ["webuild", "ngv", "nget", "lseg"] as const;

export type TenantFolder = (typeof TENANT_FOLDER_VALUES)[number];

/** Whether a `RuntimeBrand.folder` value is one of the tenants pathway ships assets for. */
export const isTenantFolder = (value: string): value is TenantFolder =>
  (TENANT_FOLDER_VALUES as readonly string[]).includes(value);

/**
 * Type-safe `brand.folder` equality. Prefer this over `brand.folder === "ngv"`
 * everywhere a tenant is branched on: the second argument is constrained to
 * `TenantFolder`, so a typo or a folder rename (`isFolder(brand, "ngvv")`) is a
 * compile error rather than a comparison that silently stops matching. This is
 * the same regression class as the earlier `brand.name` comparison bug,
 * relocated to `brand.folder`.
 */
export const isFolder = (brand: { folder: string }, folder: TenantFolder): boolean =>
  brand.folder === folder;

/**
 * Per-tenant menu icon path under `/brands/<folder>/`. `brand.folder` is
 * always populated for a real tenant pack, but the react-ui neutral
 * `FALLBACK_BRAND` (rendered for unrecognised tenant keys) reports
 * `folder: "fallback"`, which ships no `/brands/fallback/*` assets. Returns
 * `undefined` for that case — and for any other unrecognised value — so
 * callers can omit the image (or fall back to an icon) instead of requesting
 * a path that 404s.
 */
export const brandIconSrc = (folder: string, filename: string): string | undefined =>
  isTenantFolder(folder) ? `/brands/${folder}/${filename}` : undefined;

/**
 * Typed tenant packs pathway serves, keyed by folder. Unimported packs
 * tree-shake away. The `satisfies` check below fails to compile if a pack is
 * registered under a key outside `TenantFolder`, or if `TenantFolder` gains a
 * member with no corresponding pack.
 */
const tenantPacksByFolder = {
  webuild,
  ngv,
  nget,
  lseg,
} satisfies Record<TenantFolder, BrandPack>;

for (const [folder, pack] of Object.entries(tenantPacksByFolder)) {
  if (pack.folder !== folder) {
    throw new Error(
      `Tenant pack registered under "${folder}" reports folder "${pack.folder}" in tenantPacksByFolder (src/config/tenants.ts).`,
    );
  }
}

// Guard the two hand-maintained folder lists against drift. `resolveJsonModule`
// widens a JSON array to `string[]` (no literal inference), so `TenantFolder`
// cannot be derived from `tenantFolders.json` directly and the tuple above is a
// necessary second copy. The check is set-based rather than positional so that
// reordering either list — a harmless edit — does not throw; only a genuine
// difference in membership does.
{
  const unionFolders = new Set<string>(TENANT_FOLDER_VALUES);
  const jsonFolders = new Set<string>(tenantFolders);
  const inSync =
    unionFolders.size === jsonFolders.size &&
    [...unionFolders].every((folder) => jsonFolders.has(folder));

  if (!inSync) {
    throw new Error(
      "TenantFolder in src/config/tenants.ts has drifted from tenantFolders.json — update both when adding a tenant.",
    );
  }
}

/** Typed tenant packs pathway serves, in the order `BrandProvider` should try them. */
export const tenantPacks = Object.values(tenantPacksByFolder);

/**
 * Asset folders for the packs above. The list itself lives in
 * `./tenantFolders.json` so `vite.config.ts` can consume the same values at
 * config-eval time; it is re-exported here as the runtime source of truth.
 */
export { tenantFolders };

/**
 * Pathway-specific tenant-key aliases layered on top of react-auth's built-in
 * `TENANT_ALIASES`. The WeBuild Azure AD tenant authenticates as the
 * `onmicrosoft.com` domain but shares the WeBuild visual brand, so it must
 * resolve to the `webuild-ai.com` pack rather than falling back to the neutral
 * unbranded fallback. LSEG users sign in with `lseg.com` addresses but the
 * react-auth tenant key (and react-ui pack key) is the plain `lseg` string,
 * so the email domain must be aliased to it for brand resolution.
 *
 * react-auth v7 dropped the retired Invest Industrial tenant and its
 * `bi-invest.com` alias from its own tables, so pathway no longer has to
 * compensate for an upstream key it does not register. The narrowing in
 * `resolvePathwayTenantKey` stays regardless: it is what keeps any key outside
 * `TENANT_KEY_VALUES` from reaching `BrandProvider` as an unregistered pack
 * with no assets, no brand CSS and no API host.
 */
const PATHWAY_TENANT_ALIASES: Record<string, string> = {
  "webuildailimited.onmicrosoft.com": "webuild-ai.com",
  "lseg.com": "lseg",
};

/**
 * Literal union of the tenant pack keys (react-ui pack `key`) pathway resolves
 * to. This is a distinct vocabulary from `TenantFolder`: keys are what
 * `authConfig.tenant`, `BrandProvider`'s `tenantKey` prop and the email-domain
 * resolution use, whereas folders name the on-disk asset directories. Every
 * pathway `AUTH_CONFIGS` tenant resolves to one of these after alias handling
 * (`webuild-ai.com`, `ngv`, `nget`, `lseg`). Declaring it as a
 * union — and narrowing the resolvers below to it — is the key half of the
 * type-safety work that closes the `brand.name`/bare-string regression class.
 */
const TENANT_KEY_VALUES = ["webuild-ai.com", "ngv", "nget", "lseg"] as const;

export type TenantKey = (typeof TENANT_KEY_VALUES)[number];

/** The default tenant key applied when a value cannot be resolved to a known key. */
const DEFAULT_TENANT_KEY: TenantKey = "webuild-ai.com";

/** Whether an arbitrary string is one of the tenant pack keys pathway serves. */
export const isTenantKey = (value: string): value is TenantKey =>
  (TENANT_KEY_VALUES as readonly string[]).includes(value);

/**
 * Resolve an `authConfig.tenant` string to the pack key react-ui expects.
 * Handles both the built-in and pathway-specific aliases above. The result is
 * narrowed to `TenantKey` at this boundary — an unknown resolution falls back to
 * the default rather than propagating a bare `string` through the brand system.
 */
export const resolvePathwayTenantKey = (key: string): TenantKey => {
  const resolved = resolveTenantKey(key, PATHWAY_TENANT_ALIASES);

  return isTenantKey(resolved) ? resolved : DEFAULT_TENANT_KEY;
};

/**
 * Resolve a user's email domain to a known tenant pack key. Falls back to the
 * default (WeBuild) tenant for unrecognised domains. National Grid tenants
 * (NGV and NGET) cannot be distinguished by domain, so their identity comes
 * from the run mode rather than the email; callers must guard accordingly.
 */
export const tenantKeyForEmailDomain = (domain: string): TenantKey => {
  const key = getTenantForDomain(domain, PATHWAY_TENANT_ALIASES);

  return isTenantKey(key) ? key : DEFAULT_TENANT_KEY;
};

/**
 * Whether an email domain (after alias resolution) matches a tenant pathway
 * actually knows about, as opposed to silently receiving the WeBuild default
 * from `tenantKeyForEmailDomain`'s fallback. Callers should log when this is
 * false so an unrecognised tenant domain does not go unnoticed.
 */
export const isKnownTenantDomain = (domain: string): boolean =>
  isKnownTenantKey(domain, PATHWAY_TENANT_ALIASES);
